import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, Gift, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { api, ApiClientError } from '../api';
import { Logo } from '../components/Logo';
import { Loading } from '../components/Loading';
import { useToast } from '../components/Toast';

const interestOptions = [
  { tag: 'lego', label: 'Lắp ráp LEGO', icon: '🧱', tone: 'yellow' },
  { tag: 'science', label: 'Khoa học', icon: '🧪', tone: 'green' },
  { tag: 'animals', label: 'Động vật', icon: '🦁', tone: 'blue' },
  { tag: 'drawing', label: 'Vẽ & sáng tạo', icon: '🎨', tone: 'purple' },
  { tag: 'sports', label: 'Thể thao', icon: '⚽', tone: 'orange' },
  { tag: 'music', label: 'Âm nhạc', icon: '🎵', tone: 'pink' },
  { tag: 'books', label: 'Đọc sách', icon: '📚', tone: 'blue' },
  { tag: 'outdoor', label: 'Khám phá ngoài trời', icon: '🏕️', tone: 'green' }
] as const;

const rewardTypes = [
  { value: 'toy', label: 'Đồ chơi', icon: '🧸' },
  { value: 'experience', label: 'Đi chơi', icon: '🎡' },
  { value: 'book', label: 'Sách', icon: '📖' },
  { value: 'family_time', label: 'Thời gian cùng ba mẹ', icon: '👨‍👩‍👧' },
  { value: 'privilege', label: 'Quyền lựa chọn', icon: '👑' },
  { value: 'saving', label: 'Để dành mục tiêu lớn', icon: '🐷' }
] as const;

export function SurveyPage() {
  const { selectedChild, selectedChildId, refresh } = useAuth();
  const { show } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preferredTypes, setPreferredTypes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedChildId) return;
    setLoading(true);
    api<Array<{ tag: string; weight: number; source: string }>>(
      `/children/${encodeURIComponent(selectedChildId)}/interests`
    )
      .then((items) => {
        setSelected(new Set(items.filter((item) => item.source === 'survey').map((item) => item.tag)));
        setPreferredTypes(
          new Set(
            items
              .filter((item) => item.source === 'reward_type' && item.tag.startsWith('reward:'))
              .map((item) => item.tag.slice('reward:'.length))
          )
        );
      })
      .catch((error) => show(error instanceof ApiClientError ? error.message : 'Không thể tải sở thích.', 'error'))
      .finally(() => setLoading(false));
  }, [selectedChildId, show]);

  const progress = useMemo(() => Math.min(100, Math.round(((selected.size > 0 ? 1 : 0) + (preferredTypes.size > 0 ? 1 : 0)) / 2 * 100)), [selected, preferredTypes]);

  function toggleInterest(tag: string) {
    setSelected((current) => {
      const next = new Set(current);
      next.has(tag) ? next.delete(tag) : next.add(tag);
      return next;
    });
  }

  function toggleRewardType(value: string) {
    setPreferredTypes((current) => {
      const next = new Set(current);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  }

  async function save() {
    if (!selectedChildId) return;
    if (selected.size === 0) {
      show('Hãy chọn ít nhất một sở thích của con.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api(`/children/${encodeURIComponent(selectedChildId)}/interests`, {
        method: 'PUT',
        body: {
          interests: [...selected].map((tag) => ({ tag, weight: 0.9 })),
          preferredRewardTypes: [...preferredTypes]
        }
      });
      await refresh();
      show('Đã lưu hồ sơ sở thích của con.', 'success');
      navigate('/', { replace: true });
    } catch (error) {
      show(error instanceof ApiClientError ? error.message : 'Không thể lưu khảo sát.', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Loading label="Đang mở khảo sát sở thích…" />;

  return (
    <div className="survey-page page--full">
      <header className="survey-header">
        <Link to="/" className="round-back" aria-label="Quay lại"><ArrowLeft /></Link>
        <Logo compact />
        <span className="child-chip">👦 {selectedChild?.nickname}</span>
      </header>
      <div className="survey-intro">
        <span className="eyebrow"><Sparkles size={15} /> Hồ sơ cá nhân hóa</span>
        <h1>Khám phá sở thích của con</h1>
        <p>Kidee chỉ gửi nhóm tuổi và các tín hiệu sở thích, không gửi tên thật của con sang AI.</p>
        <div className="survey-progress"><div style={{ width: `${progress}%` }} /></div>
      </div>

      <section className="survey-card">
        <div className="section-heading section-heading--stack">
          <div><span className="section-icon section-icon--orange">⭐</span><h2>Con đang thích điều gì?</h2></div>
          <p>Chọn tất cả sở thích phù hợp. Bạn có thể thay đổi bất cứ lúc nào.</p>
        </div>
        <div className="interest-grid">
          {interestOptions.map((option) => {
            const active = selected.has(option.tag);
            return (
              <button
                key={option.tag}
                type="button"
                className={`interest-card interest-card--${option.tone} ${active ? 'selected' : ''}`}
                aria-pressed={active}
                onClick={() => toggleInterest(option.tag)}
              >
                <span className="interest-check">{active ? <Check size={18} /> : null}</span>
                <span className="interest-emoji" aria-hidden="true">{option.icon}</span>
                <strong>{option.label}</strong>
              </button>
            );
          })}
        </div>
      </section>

      <section className="survey-card">
        <div className="section-heading section-heading--stack">
          <div><span className="section-icon section-icon--purple"><Gift size={19} /></span><h2>Con thích kiểu phần thưởng nào?</h2></div>
          <p>AI vẫn chỉ được xếp hạng những quà ba mẹ đã thêm vào catalog.</p>
        </div>
        <div className="choice-pills">
          {rewardTypes.map((type) => {
            const active = preferredTypes.has(type.value);
            return (
              <button
                key={type.value}
                type="button"
                className={active ? 'selected' : ''}
                aria-pressed={active}
                onClick={() => toggleRewardType(type.value)}
              >
                <span>{type.icon}</span>{type.label}{active && <Check size={15} />}
              </button>
            );
          })}
        </div>
      </section>

      <div className="sticky-action">
        <button className="button button--primary button--wide" type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Lưu sở thích'}
        </button>
        <Link to="/" className="text-link">Để sau</Link>
      </div>
    </div>
  );
}
